import React, { useState, useRef, useEffect } from 'react';

export default function RepeatAudio() {
  const [audioUrl, setAudioUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(true);
  const [isYouTube, setIsYouTube] = useState(false);
  const [youtubeVideoId, setYoutubeVideoId] = useState('');
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isCustomLoop, setIsCustomLoop] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [volume, setVolume] = useState(50);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [showAdvancedControls, setShowAdvancedControls] = useState(false);
  
  const audioRef = useRef(null);
  const youtubePlayerRef = useRef(null);
  const intervalRef = useRef(null);
  const timeUpdateRef = useRef(null);

  // Default URL từ yêu cầu
  const defaultUrl = 'https://s4-media1.study4.com/media/tez_media/sound/ets_toeic_2020_test_2_11.mp3';

  // Hàm chuyển đổi giây thành định dạng mm:ss
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return '0:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Hàm chuyển đổi từ định dạng mm:ss thành giây (cải tiến)
  const parseTimeInput = (timeString) => {
    if (!timeString || timeString.trim() === '') return 0;
    
    // Remove any extra spaces and convert to string
    const cleanString = String(timeString).trim();
    
    // If it's just a number, treat as seconds
    if (/^\d+$/.test(cleanString)) {
      return Math.max(0, parseInt(cleanString));
    }
    
    // Parse mm:ss format
    const parts = cleanString.split(':');
    if (parts.length === 2) {
      const minutes = Math.max(0, parseInt(parts[0]) || 0);
      const seconds = Math.max(0, Math.min(59, parseInt(parts[1]) || 0));
      return minutes * 60 + seconds;
    }
    
    // If format is wrong, return 0
    return 0;
  };

  // Hàm tách thời gian thành phút và giây
  const getTimeComponents = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return { minutes, seconds };
  };

  // Hàm set thời gian từ phút và giây
  const setTimeFromComponents = (minutes, seconds, isStart = true) => {
    const totalSeconds = (parseInt(minutes) || 0) * 60 + (parseInt(seconds) || 0);
    if (isStart) {
      setStartTime(Math.max(0, Math.min(totalSeconds, duration)));
    } else {
      setEndTime(Math.max(0, Math.min(totalSeconds, duration)));
    }
  };

  // Hàm tính progress percentage
  const getProgress = () => {
    if (!duration) return 0;
    return (currentTime / duration) * 100;
  };

  // Hàm tính progress của custom loop
  const getCustomLoopProgress = () => {
    if (!isCustomLoop || startTime >= endTime) return 0;
    const loopDuration = endTime - startTime;
    const loopCurrentTime = currentTime - startTime;
    return Math.max(0, Math.min(100, (loopCurrentTime / loopDuration) * 100));
  };

  // Hàm điều chỉnh volume
  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    
    if (isYouTube && youtubePlayerRef.current) {
      youtubePlayerRef.current.setVolume(newVolume);
    } else if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
  };

  // Hàm điều chỉnh tốc độ phát
  const handlePlaybackRateChange = (newRate) => {
    setPlaybackRate(newRate);
    
    if (isYouTube && youtubePlayerRef.current) {
      youtubePlayerRef.current.setPlaybackRate(newRate);
    } else if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  };

  // Preset tốc độ
  const speedPresets = [
    { label: '0.5x', value: 0.5 },
    { label: '0.75x', value: 0.75 },
    { label: '1x', value: 1.0 },
    { label: '1.25x', value: 1.25 },
    { label: '1.5x', value: 1.5 },
    { label: '2x', value: 2.0 }
  ];

  // Hàm cập nhật thời gian hiện tại
  const updateCurrentTime = () => {
    if (isYouTube && youtubePlayerRef.current) {
      const time = youtubePlayerRef.current.getCurrentTime();
      setCurrentTime(time);
      
      // Kiểm tra nếu đang ở chế độ custom loop
      if (isCustomLoop && endTime > startTime) {
        if (time >= endTime) {
          youtubePlayerRef.current.seekTo(startTime);
        }
      }
    } else if (audioRef.current) {
      const time = audioRef.current.currentTime;
      setCurrentTime(time);
      
      // Kiểm tra nếu đang ở chế độ custom loop
      if (isCustomLoop && endTime > startTime) {
        if (time >= endTime) {
          audioRef.current.currentTime = startTime;
        }
      }
    }
  };

  useEffect(() => {
    setAudioUrl(defaultUrl);
    
    // Load YouTube IFrame API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // Set up global callback for YouTube API
    window.onYouTubeIframeAPIReady = () => {
      console.log('YouTube API Ready');
    };

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeUpdateRef.current) {
        clearInterval(timeUpdateRef.current);
      }
    };
  }, []);

  // Effect để cập nhật thời gian liên tục khi đang phát
  useEffect(() => {
    if (isPlaying) {
      timeUpdateRef.current = setInterval(updateCurrentTime, 100);
    } else {
      if (timeUpdateRef.current) {
        clearInterval(timeUpdateRef.current);
        timeUpdateRef.current = null;
      }
    }

    return () => {
      if (timeUpdateRef.current) {
        clearInterval(timeUpdateRef.current);
        timeUpdateRef.current = null;
      }
    };
  }, [isPlaying, isCustomLoop, startTime, endTime]);

  // Effect để apply volume và playback rate khi audio được load
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
      audioRef.current.playbackRate = playbackRate;
    }
  }, [audioUrl, volume, playbackRate]);

  // Function to extract YouTube video ID from URL
  const extractYouTubeVideoId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  // Function to check if URL is YouTube
  const isYouTubeUrl = (url) => {
    return /^(https?\:\/\/)?(www\.)?(youtube\.com|youtu\.be)/.test(url);
  };

  const handlePlayPause = () => {
    setError('');
    
    if (isYouTube && youtubePlayerRef.current) {
      if (isPlaying) {
        youtubePlayerRef.current.pauseVideo();
        setIsPlaying(false);
      } else {
        // Nếu đang ở chế độ custom loop, bắt đầu từ startTime
        if (isCustomLoop && startTime > 0) {
          youtubePlayerRef.current.seekTo(startTime);
        }
        youtubePlayerRef.current.playVideo();
        setIsPlaying(true);
      }
    } else if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Nếu đang ở chế độ custom loop, bắt đầu từ startTime
        if (isCustomLoop && startTime > 0) {
          audioRef.current.currentTime = startTime;
        }
        audioRef.current.play().catch(err => {
          setError('Không thể phát audio. Vui lòng kiểm tra URL.');
          console.error('Audio play error:', err);
        });
        setIsPlaying(true);
      }
    }
  };

  const handleRepeatToggle = () => {
    setIsLooping(!isLooping);
    if (!isYouTube && audioRef.current) {
      audioRef.current.loop = !isLooping;
    }
  };

  // Hàm toggle custom loop
  const handleCustomLoopToggle = () => {
    setIsCustomLoop(!isCustomLoop);
    if (!isCustomLoop) {
      // Khi bật custom loop, tắt loop thông thường
      setIsLooping(false);
      if (!isYouTube && audioRef.current) {
        audioRef.current.loop = false;
      }
    }
  };

  // Hàm jump tới thời gian bắt đầu
  const jumpToStart = () => {
    if (isYouTube && youtubePlayerRef.current) {
      youtubePlayerRef.current.seekTo(startTime);
    } else if (audioRef.current) {
      audioRef.current.currentTime = startTime;
    }
  };

  // Hàm jump tới thời gian kết thúc
  const jumpToEnd = () => {
    if (isYouTube && youtubePlayerRef.current) {
      youtubePlayerRef.current.seekTo(endTime);
    } else if (audioRef.current) {
      audioRef.current.currentTime = endTime;
    }
  };

  // Hàm seek đến thời gian cụ thể
  const handleSeek = (percentage) => {
    const newTime = (percentage / 100) * duration;
    if (isYouTube && youtubePlayerRef.current) {
      youtubePlayerRef.current.seekTo(newTime);
    } else if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const createYouTubePlayer = (videoId) => {
    if (window.YT && window.YT.Player) {
      setIsLoading(true);
      
      // Destroy existing player if any
      if (youtubePlayerRef.current && youtubePlayerRef.current.destroy) {
        youtubePlayerRef.current.destroy();
      }

      youtubePlayerRef.current = new window.YT.Player('youtube-player', {
        height: '0',
        width: '0',
        videoId: videoId,
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          enablejsapi: 1,
          fs: 0,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          showinfo: 0
        },
        events: {
          onReady: (event) => {
            console.log('YouTube player ready');
            setIsLoading(false);
            // Lấy thông tin duration
            const playerDuration = youtubePlayerRef.current.getDuration();
            setDuration(playerDuration);
            if (endTime === 0) {
              setEndTime(playerDuration);
            }
            // Apply volume và playback rate
            youtubePlayerRef.current.setVolume(volume);
            youtubePlayerRef.current.setPlaybackRate(playbackRate);
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              if (isLooping && !isCustomLoop) {
                // Check for video end and restart if looping
                intervalRef.current = setInterval(() => {
                  const duration = youtubePlayerRef.current.getDuration();
                  const currentTime = youtubePlayerRef.current.getCurrentTime();
                  if (duration - currentTime < 1) {
                    youtubePlayerRef.current.seekTo(0);
                  }
                }, 1000);
              }
            } else if (event.data === window.YT.PlayerState.PAUSED) {
              setIsPlaying(false);
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
            } else if (event.data === window.YT.PlayerState.ENDED) {
              setIsPlaying(false);
              if (isLooping && !isCustomLoop) {
                youtubePlayerRef.current.seekTo(0);
                youtubePlayerRef.current.playVideo();
              }
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
            }
          },
          onError: (event) => {
            setIsLoading(false);
            setError('Lỗi khi tải YouTube video. Vui lòng kiểm tra URL.');
            console.error('YouTube player error:', event);
          }
        }
      });
    } else {
      // YouTube API not ready, try again after a delay
      setTimeout(() => createYouTubePlayer(videoId), 500);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setAudioUrl(url);
    setIsPlaying(false);
    setError('');
    
    // Reset time values
    setCurrentTime(0);
    setStartTime(0);
    setEndTime(0);
    setDuration(0);
    
    // Clear any existing intervals
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (isYouTubeUrl(url)) {
      const videoId = extractYouTubeVideoId(url);
      if (videoId) {
        setIsYouTube(true);
        setYoutubeVideoId(videoId);
        setTimeout(() => createYouTubePlayer(videoId), 100);
      } else {
        setIsYouTube(false);
        setYoutubeVideoId('');
        setError('URL YouTube không hợp lệ');
      }
    } else {
      setIsYouTube(false);
      setYoutubeVideoId('');
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.load();
      }
    }
  };

  const handleAudioEnded = () => {
    if (!isLooping && !isCustomLoop) {
      setIsPlaying(false);
    }
  };

  const handleAudioError = () => {
    setError('Lỗi khi tải file audio. Vui lòng kiểm tra URL.');
    setIsPlaying(false);
    setIsLoading(false);
  };

  // Hàm xử lý khi audio được load
  const handleAudioLoaded = () => {
    if (audioRef.current) {
      setIsLoading(false);
      const audioDuration = audioRef.current.duration;
      setDuration(audioDuration);
      if (endTime === 0) {
        setEndTime(audioDuration);
      }
      // Apply volume và playback rate
      audioRef.current.volume = volume / 100;
      audioRef.current.playbackRate = playbackRate;
    }
  };

  const handleAudioLoadStart = () => {
    setIsLoading(true);
    setError('');
  };

  // Component SimpleTimeInput - đơn giản và dễ dùng (cải tiến)
  const SimpleTimeInput = ({ label, timeValue, onChange, onSetCurrent, onJump, isStartTime = true }) => {
    const [inputValue, setInputValue] = useState(formatTime(timeValue || 0));
    const [isEditing, setIsEditing] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Update input value when timeValue changes from external sources
    useEffect(() => {
      if (!isEditing) {
        setInputValue(formatTime(timeValue || 0));
        setHasError(false);
      }
    }, [timeValue, isEditing]);

    const handleInputChange = (e) => {
      const value = e.target.value;
      setInputValue(value);
      setHasError(false);
      
      // Auto-format while typing
      if (value.length === 1 && /^\d$/.test(value)) {
        // If user types single digit, assume it's minutes and add :0
        if (parseInt(value) > 5) {
          setInputValue(`0:${value}0`);
        }
      } else if (value.length === 2 && /^\d{2}$/.test(value)) {
        // If user types two digits like "12", format as "1:20" if > 59
        const num = parseInt(value);
        if (num > 59) {
          const minutes = Math.floor(num / 10);
          const seconds = (num % 10) * 10;
          setInputValue(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }
      }
    };

    const handleInputBlur = () => {
      setIsEditing(false);
      
      try {
        const newTime = parseTimeInput(inputValue);
        const maxTime = duration || 999999;
        
        if (isNaN(newTime) || newTime < 0) {
          setHasError(true);
          setInputValue(formatTime(timeValue || 0));
          return;
        }
        
        const clampedTime = Math.max(0, Math.min(newTime, maxTime));
        
        // Validation for start/end time logic
        if (isStartTime && endTime > 0 && clampedTime >= endTime) {
          setHasError(true);
          setInputValue(formatTime(timeValue || 0));
          return;
        }
        
        if (!isStartTime && startTime >= 0 && clampedTime <= startTime) {
          setHasError(true);
          setInputValue(formatTime(timeValue || 0));
          return;
        }
        
        onChange(clampedTime);
        setInputValue(formatTime(clampedTime));
        setHasError(false);
      } catch (error) {
        console.error('Error parsing time:', error);
        setHasError(true);
        setInputValue(formatTime(timeValue || 0));
      }
    };

    const handleInputFocus = () => {
      setIsEditing(true);
      setHasError(false);
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.target.blur();
      }
      
      // Only allow numbers, colon, and backspace
      if (!/[\d:]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
        e.preventDefault();
      }
    };

    const setQuickTime = (seconds) => {
      const safeSeconds = Math.max(0, Math.min(seconds, duration || 999999));
      onChange(safeSeconds);
      setInputValue(formatTime(safeSeconds));
      setHasError(false);
    };

    const handleTimelineClick = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const percentage = ((e.clientX - rect.left) / rect.width) * 100;
      const newTime = (percentage / 100) * (duration || 0);
      const clampedTime = Math.max(0, Math.min(newTime, duration || 0));
      setQuickTime(Math.floor(clampedTime));
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-orange-400 font-mono">
            {label.toUpperCase().replace(' ', '_')}
            {isPlaying && <span className="text-xs text-cyan-400 ml-2">(Input disabled while playing)</span>}
          </label>
          <div className="text-xs text-orange-300 font-mono">
            Max: {formatTime(duration)}
          </div>
        </div>

        <div className="space-y-4">
          {/* Main Time Input */}
          <div className="relative">
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyPress={handleKeyPress}
              placeholder="0:00"
              disabled={isPlaying}
              className={`w-full px-4 py-3 text-center text-xl font-mono border rounded-lg focus:ring-2 outline-none transition-all backdrop-blur-sm ${
                isPlaying 
                  ? 'bg-black/40 border-gray-500/50 text-gray-400 cursor-not-allowed'
                  : hasError 
                    ? 'border-red-400/50 focus:ring-red-500 focus:border-red-400 bg-red-500/10 text-red-400 shadow-neon-pink' 
                    : 'border-orange-400/50 focus:ring-orange-500 focus:border-orange-400 bg-black/60 text-orange-300 shadow-neon-orange'
              }`}
            />
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <span className={`text-sm font-mono ${hasError ? 'text-red-400' : 'text-orange-400'}`}>
                mm:ss
              </span>
            </div>
            {hasError && (
              <div className="absolute -bottom-6 left-0 text-xs text-red-400 font-mono">
                [ERROR] {isStartTime ? 'Start time must be less than end time' : 'End time must be greater than start time'}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                const currentTimeToSet = Math.floor(currentTime);
                onSetCurrent();
                setInputValue(formatTime(currentTimeToSet));
                setHasError(false);
              }}
              className={`px-4 py-2 rounded-lg transition-colors text-sm font-mono font-medium border ${
                isPlaying 
                  ? 'bg-green-400/20 text-green-400 border-green-400/50 hover:bg-green-400/30 shadow-neon-green' 
                  : 'bg-cyan-400/20 text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/30 shadow-neon-cyan'
              }`}
            >
              📍 SET CURRENT ({formatTime(currentTime)})
            </button>
            <button
              onClick={onJump}
              disabled={isPlaying}
              className={`px-4 py-2 rounded-lg transition-colors text-sm font-mono font-medium border ${
                isPlaying
                  ? 'bg-black/40 text-gray-500 border-gray-500/50 cursor-not-allowed'
                  : 'bg-pink-400/20 text-pink-400 border-pink-400/50 hover:bg-pink-400/30 shadow-neon-pink'
              }`}
            >
              ⚡ JUMP TEST
            </button>
          </div>

          {/* Quick Time Shortcuts */}
          <div>
            <div className="text-xs text-orange-300 mb-2 font-mono">
              QUICK SET: {isPlaying && <span className="text-green-400">(Works while playing)</span>}
            </div>
            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => setQuickTime(0)}
                className="px-3 py-2 text-sm bg-black/60 text-orange-300 border border-orange-400/30 rounded-lg hover:bg-orange-400/10 hover:border-orange-400/50 transition-colors font-mono"
              >
                0:00
              </button>
              <button
                onClick={() => setQuickTime(30)}
                className="px-3 py-2 text-sm bg-black/60 text-orange-300 border border-orange-400/30 rounded-lg hover:bg-orange-400/10 hover:border-orange-400/50 transition-colors font-mono"
              >
                0:30
              </button>
              <button
                onClick={() => setQuickTime(60)}
                className="px-3 py-2 text-sm bg-black/60 text-orange-300 border border-orange-400/30 rounded-lg hover:bg-orange-400/10 hover:border-orange-400/50 transition-colors font-mono"
              >
                1:00
              </button>
              <button
                onClick={() => setQuickTime(isStartTime ? 0 : duration)}
                className="px-3 py-2 text-sm bg-cyan-400/20 text-cyan-400 border border-cyan-400/50 rounded-lg hover:bg-cyan-400/30 hover:border-cyan-400/70 transition-colors font-mono shadow-neon-cyan"
              >
                {isStartTime ? 'START' : 'END'}
              </button>
            </div>
          </div>

          {/* Visual Progress Indicator */}
          <div className="relative">
            <div className="text-xs text-orange-300 mb-1 font-mono">
              TIMELINE: {isStartTime ? 'Click to set start time' : 'Click to set end time'}
              {isPlaying && <span className="text-green-400 ml-1">(Works while playing)</span>}
            </div>
            <div 
              className="w-full h-8 bg-black/60 border border-orange-400/30 rounded-lg cursor-pointer relative overflow-hidden hover:bg-orange-400/10 hover:border-orange-400/50 transition-colors backdrop-blur-sm"
              onClick={handleTimelineClick}
            >
              {/* Progress background */}
              <div 
                className="absolute top-0 left-0 h-full bg-orange-400/20 transition-all"
                style={{ width: `${(timeValue / duration) * 100}%` }}
              />
              
              {/* Current time indicator */}
              <div 
                className="absolute top-0 h-full w-1 bg-green-400 shadow-neon-green transition-all"
                style={{ left: `${(currentTime / duration) * 100}%` }}
              />
              
              {/* Time display */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-mono text-orange-300">
                  {formatTime(timeValue)} / {formatTime(duration)}
                </span>
              </div>
              
              {/* Click indicator */}
              <div 
                className="absolute top-0 h-full w-2 bg-orange-400 shadow-neon-orange rounded-full transition-all"
                style={{ left: `${Math.max(0, Math.min((timeValue / duration) * 100 - 1, 98))}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

      return (
      <div className="min-h-screen bg-black overflow-hidden relative pt-20 px-4">
        {/* Cyberpunk Grid Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(cyan 1px, transparent 1px),
              linear-gradient(90deg, cyan 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            animation: 'gridPulse 4s ease-in-out infinite'
          }}></div>
        </div>
        
        {/* Holographic Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-cyan-400/20 to-blue-600/20 rounded-full blur-3xl animate-pulse-neon"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse-neon delay-1000"></div>
        </div>
        
        <div className="max-w-4xl mx-auto pb-5 relative z-10">
        {/* Cyberpunk Header */}
        <div className="text-center mb-8 relative">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-black/60 border-2 border-cyan-400 rounded-2xl mb-6 shadow-neon-cyan group hover:scale-110 transition-transform duration-300">
            <svg className="w-10 h-10 text-cyan-400 group-hover:text-pink-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            <div className="absolute inset-0 rounded-2xl"></div>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-green-400 mb-4 font-mono tracking-wider animate-gradient-x">
            NEURAL AUDIO PLAYER
          </h1>
          <p className="text-lg text-gray-300 font-mono max-w-2xl mx-auto">
            <span className="text-cyan-400">{'>'}</span> Professional neural audio learning tool<br/>
            <span className="text-pink-400">{'>'}</span> Advanced loop & repeat protocols
            <span className="animate-blink text-green-400">█</span>
          </p>
        </div>
          
        {/* Vertical Stack Layout */}
          <div className="space-y-6">
          {/* Audio URL Input Card */}
          <div className="bg-black/80 border border-cyan-400/50 rounded-2xl shadow-neon-cyan p-6 backdrop-blur-sm">
            <div className="flex items-center mb-4">
              <svg className="w-5 h-5 text-cyan-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h2 className="text-xl font-bold text-cyan-400 font-mono">AUDIO SOURCE</h2>
            </div>
            
            <div className="space-y-4">
            <div>
              <label className="block text-sm font-mono font-medium text-gray-300 mb-2">
                  NEURAL AUDIO INPUT
              </label>
              <input
                type="url"
                value={audioUrl}
                onChange={handleUrlChange}
                  placeholder="https://example.com/audio.mp3 or https://youtube.com/watch?v=..."
                  className="w-full px-4 py-3 bg-black/60 border border-cyan-400/50 rounded-xl text-white placeholder-gray-500 font-mono focus:ring-2 focus:ring-cyan-400 focus:border-transparent outline-none transition-all duration-300"
              />
              </div>
              
              {isYouTube && (
                <div className="flex items-center text-sm text-green-400 bg-green-500/20 border border-green-400/50 rounded-lg px-3 py-2 font-mono">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  YOUTUBE_DETECTED • ID: {youtubeVideoId}
                </div>
              )}
              
              {error && (
                <div className="flex items-center text-sm text-red-400 bg-red-500/20 border border-red-400/50 rounded-lg px-3 py-2 font-mono">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  ERROR {error}
                </div>
              )}
            </div>
            </div>

          {/* Player Card */}
            {audioUrl && (
            <div className="bg-black/80 border border-pink-400/50 rounded-2xl shadow-neon-pink p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-pink-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h1m4 0h1" />
                  </svg>
                  <h2 className="text-xl font-bold text-pink-400 font-mono">AUDIO PLAYER</h2>
                </div>
                
                {isLoading && (
                  <div className="flex items-center text-cyan-400 font-mono">
                    <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    LOADING...
                  </div>
                )}
              </div>

              {/* Progress Bar Section */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-300 mb-2">
                  <span className="font-mono text-cyan-400">{formatTime(currentTime)}</span>
                  <span className="font-mono text-pink-400">{formatTime(duration)}</span>
                </div>
                
                <div className="relative">
                  {/* Main progress bar */}
                  <div 
                    className="w-full h-3 bg-black/60 border border-cyan-400/30 rounded-full cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const percentage = ((e.clientX - rect.left) / rect.width) * 100;
                      handleSeek(percentage);
                    }}
                  >
                    <div
                      className="h-full bg-gradient-to-r from-cyan-400 to-pink-500 rounded-full transition-all duration-150 shadow-neon-cyan"
                      style={{ width: `${getProgress()}%` }}
                    />
                  </div>
                  
                  {/* Custom loop indicators */}
                  {isCustomLoop && startTime < endTime && (
                    <div className="absolute top-0 h-3 rounded-full">
                      <div
                        className="absolute h-full bg-yellow-400 opacity-50 rounded-full"
                        style={{
                          left: `${(startTime / duration) * 100}%`,
                          width: `${((endTime - startTime) / duration) * 100}%`
                        }}
                      />
                      <div
                        className="absolute h-full bg-green-500 rounded-full"
                        style={{
                          left: `${(startTime / duration) * 100}%`,
                          width: `${getCustomLoopProgress() * ((endTime - startTime) / duration) / 100 * 100}%`
                        }}
                      />
                    </div>
                  )}
                </div>
                
                {isCustomLoop && (
                  <div className="flex justify-between text-xs text-green-400 mt-2 font-mono">
                    <span>LOOP START: {formatTime(startTime)}</span>
                    <span>LOOP END: {formatTime(endTime)}</span>
                  </div>
                )}
              </div>

              {/* Main Controls Section */}
              <div className="flex items-center justify-center space-x-4 mb-6">
                <button
                  onClick={() => handleSeek(Math.max(0, getProgress() - 5))}
                  disabled={!audioUrl}
                  className="p-3 rounded-full bg-black/60 border border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10 hover:text-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-neon-cyan"
                  title="Rewind 5%"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                  </svg>
                </button>
                
                <button
                  onClick={handlePlayPause}
                  disabled={!audioUrl || isLoading}
                  className={`p-4 rounded-full text-white transition-all duration-300 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none font-mono ${
                    isPlaying
                      ? 'bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 shadow-neon-pink'
                      : 'bg-gradient-to-r from-green-500 to-cyan-600 hover:from-green-600 hover:to-cyan-700 shadow-neon-green'
                  }`}
                >
                  {isPlaying ? (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                    </svg>
                  ) : (
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  )}
                </button>
                
                <button
                  onClick={() => handleSeek(Math.min(100, getProgress() + 5))}
                  disabled={!audioUrl}
                  className="p-3 rounded-full bg-black/60 border border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10 hover:text-cyan-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-neon-cyan"
                  title="Forward 5%"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
                  </svg>
                </button>
              </div>

              {/* Advanced Controls Toggle */}
              <div className="flex items-center justify-center mb-4">
                <button
                  onClick={() => setShowAdvancedControls(!showAdvancedControls)}
                  className="flex items-center px-4 py-2 text-sm text-purple-400 bg-black/60 border border-purple-400/50 hover:bg-purple-400/10 rounded-lg transition-all duration-300 font-mono shadow-neon-purple"
                >
                  <svg 
                    className={`w-4 h-4 mr-2 transition-transform duration-300 ${showAdvancedControls ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  {showAdvancedControls ? 'HIDE ADVANCED' : 'SHOW ADVANCED'}
                </button>
              </div>

              {/* Advanced Controls Section */}
              {showAdvancedControls && (
                <div className="space-y-4 animate-in slide-in-from-top duration-300">
                  {/* Volume and Speed Controls */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-black/40 border border-purple-400/30 rounded-xl backdrop-blur-sm">
                    {/* Volume Control */}
                  <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-purple-400 flex items-center font-mono">
                          <svg className="w-4 h-4 mr-1 text-purple-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                          </svg>
                          VOLUME
                        </label>
                        <span className="text-sm text-purple-300 font-mono">{volume}%</span>
                        </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={volume}
                        onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                        className="w-full h-2 bg-black/60 border border-purple-400/30 rounded-lg appearance-none cursor-pointer shadow-neon-purple"
                        style={{
                          background: `linear-gradient(to right, #a855f7 0%, #a855f7 ${volume}%, rgba(0,0,0,0.6) ${volume}%, rgba(0,0,0,0.6) 100%)`
                        }}
                      />
                        </div>

                    {/* Speed Control */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-orange-400 flex items-center font-mono">
                          <svg className="w-4 h-4 mr-1 text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M13,2.05V5.08C16.39,5.57 19,8.47 19,12C19,12.9 18.82,13.75 18.5,14.54L21.12,16.07C21.68,14.83 22,13.45 22,12C22,6.82 18.05,2.55 13,2.05M12,19A7,7 0 0,1 5,12C5,8.47 7.61,5.57 11,5.08V2.05C5.94,2.55 2,6.81 2,12A10,10 0 0,0 12,22C15.3,22 18.23,20.39 20.07,17.93L17.45,16.4C15.9,18.75 14.13,19 12,19Z"/>
                          </svg>
                          SPEED
                        </label>
                        <span className="text-sm text-orange-300 font-mono">{playbackRate}x</span>
                        </div>
                      <div className="flex flex-wrap gap-1">
                        {speedPresets.map((preset) => (
                          <button
                            key={preset.value}
                            onClick={() => handlePlaybackRateChange(preset.value)}
                            className={`px-3 py-1 text-xs rounded-lg transition-colors font-mono border ${
                              playbackRate === preset.value
                                ? 'bg-orange-400/20 text-orange-400 border-orange-400/50 shadow-neon-orange'
                                : 'bg-black/60 text-orange-300 border-orange-400/30 hover:bg-orange-400/10 hover:border-orange-400/50'
                            }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Advanced Loop Controls */}
                  <div className="flex items-center justify-center space-x-3">
                    <button
                      onClick={handleRepeatToggle}
                      disabled={!audioUrl || isCustomLoop}
                      className={`px-6 py-3 rounded-lg font-mono font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border ${
                        isLooping && !isCustomLoop
                          ? 'bg-cyan-400/20 text-cyan-400 border-cyan-400/50 shadow-neon-cyan'
                          : 'bg-black/60 text-cyan-300 border-cyan-400/30 hover:bg-cyan-400/10 hover:border-cyan-400/50 hover:shadow-neon-cyan'
                      }`}
                    >
                      <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
                      </svg>
                      FULL REPEAT
                    </button>
                    
                    <button
                      onClick={handleCustomLoopToggle}
                      disabled={!audioUrl || startTime >= endTime}
                      className={`px-6 py-3 rounded-lg font-mono font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border ${
                        isCustomLoop
                          ? 'bg-green-400/20 text-green-400 border-green-400/50 shadow-neon-green'
                          : 'bg-black/60 text-green-300 border-green-400/30 hover:bg-green-400/10 hover:border-green-400/50 hover:shadow-neon-green'
                      }`}
                    >
                      <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      CUSTOM LOOP
                    </button>
                  </div>
                </div>
              )}

              {/* Status Display */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm">
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${isPlaying ? 'bg-green-500 shadow-neon-green animate-pulse' : 'bg-gray-500'}`}></div>
                  <span className="text-gray-300 font-mono">{isPlaying ? 'PLAYING' : 'PAUSED'}</span>
                </div>
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${
                    isCustomLoop ? 'bg-green-500 shadow-neon-green' : isLooping ? 'bg-cyan-500 shadow-neon-cyan' : 'bg-gray-500'
                  }`}></div>
                  <span className="text-gray-300 font-mono">
                    {isCustomLoop ? 'CUSTOM LOOP' : isLooping ? 'FULL REPEAT' : 'PLAY ONCE'}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${isYouTube ? 'bg-red-500 shadow-neon-pink' : 'bg-cyan-500 shadow-neon-cyan'}`}></div>
                  <span className="text-gray-300 font-mono">{isYouTube ? '[YOUTUBE]' : 'AUDIO FILE'}</span>
                </div>
                {showAdvancedControls && (
                  <>
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full mr-2 bg-purple-500 shadow-neon-purple"></div>
                      <span className="text-gray-300 font-mono">VOL: {volume}%</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full mr-2 bg-orange-500 shadow-neon-orange"></div>
                      <span className="text-gray-300 font-mono">SPEED: {playbackRate}x</span>
                    </div>
                  </>
                )}
              </div>

              {/* Hidden Audio/YouTube Element */}
              {isYouTube ? (
                <div id="youtube-player" style={{ display: 'none' }}></div>
                ) : (
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                  loop={isLooping && !isCustomLoop}
                    onEnded={handleAudioEnded}
                    onError={handleAudioError}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  onLoadedMetadata={handleAudioLoaded}
                  onLoadStart={handleAudioLoadStart}
                  style={{ display: 'none' }}
                  />
                )}
              </div>
            )}

          {/* Custom Loop Settings Card */}
          {audioUrl && showAdvancedControls && (
            <div className="bg-black/80 border border-orange-400/50 rounded-2xl shadow-neon-orange p-6 backdrop-blur-sm animate-in slide-in-from-top duration-300">
              <div className="flex items-center mb-6">
                <svg className="w-5 h-5 text-orange-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="text-xl font-bold text-orange-400 font-mono">CUSTOM LOOP SETTINGS</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  {/* Start Time */}
                  <div>
                    <SimpleTimeInput
                      label="Start Time"
                      timeValue={startTime}
                      onChange={(newTime) => setStartTime(newTime)}
                      onSetCurrent={() => setStartTime(currentTime)}
                      onJump={jumpToStart}
                      isStartTime={true}
                    />
                  </div>

                  {/* End Time */}
                  <div>
                    <SimpleTimeInput
                      label="End Time"
                      timeValue={endTime}
                      onChange={(newTime) => setEndTime(newTime)}
                      onSetCurrent={() => setEndTime(currentTime)}
                      onJump={jumpToEnd}
                      isStartTime={false}
                    />
                  </div>
            </div>

              {startTime >= endTime && audioUrl && (
                <div className="mt-4 p-3 bg-red-500/20 border border-red-400/50 rounded-lg backdrop-blur-sm">
                  <div className="flex items-center text-sm text-red-400 font-mono">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                    </svg>
                    [ERROR] Start time must be less than end time
                  </div>
                </div>
              )}

              {isCustomLoop && (
                <div className="mt-4 p-4 bg-green-500/20 border border-green-400/50 rounded-lg backdrop-blur-sm">
            <div className="text-center">
                    <div className="text-green-400 font-mono font-bold mb-1">
                      [✓] CUSTOM LOOP ACTIVE
                    </div>
                    <div className="text-sm text-green-300 font-mono">
                      {'>'} Looping: {formatTime(startTime)} → {formatTime(endTime)}
                    </div>
                    <div className="text-xs text-green-400 mt-1 font-mono">
                      {'>'} Duration: {formatTime(endTime - startTime)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cyberpunk Quick Guide */}
          <div className="bg-black/80 border border-cyan-400/50 rounded-2xl shadow-neon-cyan p-6 backdrop-blur-sm relative overflow-hidden">
            {/* Animated border */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-slide-right rounded-t-2xl"></div>
            
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-black/60 border border-cyan-400/50 rounded-xl flex items-center justify-center mr-3 shadow-neon-cyan">
                <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-cyan-400 font-mono">NEURAL USAGE GUIDE</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm font-mono">
              <div className="space-y-3">
                <div className="flex items-start">
                  <span className="font-bold mr-3 text-pink-400 bg-pink-400/20 px-2 py-1 rounded border border-pink-400/30">[1]</span>
                  <span className="text-gray-300">
                    <span className="text-cyan-400">{'>'}</span> Paste audio URL or YouTube link
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="font-bold mr-3 text-green-400 bg-green-400/20 px-2 py-1 rounded border border-green-400/30">[2]</span>
                  <span className="text-gray-300">
                    <span className="text-cyan-400">{'>'}</span> Use play controls to start/pause
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="font-bold mr-3 text-purple-400 bg-purple-400/20 px-2 py-1 rounded border border-purple-400/30">[3]</span>
                  <span className="text-gray-300">
                    <span className="text-cyan-400">{'>'}</span> Click "SHOW_ADVANCED" for more features
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <span className="font-bold mr-3 text-orange-400 bg-orange-400/20 px-2 py-1 rounded border border-orange-400/30">[4]</span>
                  <span className="text-gray-300">
                    <span className="text-cyan-400">{'>'}</span> Adjust volume and playback speed
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="font-bold mr-3 text-yellow-400 bg-yellow-400/20 px-2 py-1 rounded border border-yellow-400/30">[5]</span>
                  <span className="text-gray-300">
                    <span className="text-cyan-400">{'>'}</span> Set custom loop times for practice
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="font-bold mr-3 text-indigo-400 bg-indigo-400/20 px-2 py-1 rounded border border-indigo-400/30">[6]</span>
                  <span className="text-gray-300">
                    <span className="text-cyan-400">{'>'}</span> Use repeat modes for focused learning
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 